import React, { useEffect, useState, useCallback, useMemo } from "react";
import { DatePicker, Select } from "antd";
import { TableData } from "../../../core/data/interface";
import { RootState } from "../../../core/data/redux/store";
import Table from "../../../core/common/dataTable/index";
import ImageWithBasePath from "../../../core/common/imageWithBasePath";
import { Link } from "react-router-dom";
import { all_routes } from "../../router/all_routes";
import TooltipOption from "../../../core/common/tooltipOption";
import { useDispatch, useSelector } from "react-redux";
import { fetchattendancesSuccess } from "../../../core/data/redux/attendanceSlice";
import { fetchAttendancesApi } from "../../../core/data/services/attendanceService";
import { fetchclassesApi } from "../../../core/data/services/classService";
import {
  setMealApi,
  fetchMealsApi,
  checkMealSubmissionExistsApi,
  mealReasonApi
} from "../../../core/data/services/mealService";
import { fetchmealsSuccess } from "../../../core/data/redux/mealSlice";
import { fetchusersSuccess } from "../../../core/data/redux/userSlice";
import SuccessModal from '../../../core/common/successModal';
import ErrorModal from '../../../core/common/ErrorModal';
import {
  exportToPDF,
  exportToExcel,
  ExportColumn,
} from "../../../utils/exportUtils"

const { Option } = Select;

const StudentMeals = () => {
  const routes = all_routes;
  const dispatch = useDispatch();
  const currentDate = new Date().toISOString().split("T")[0];

  const user = useSelector((state: RootState) => state.user.Users);
  const attendance = useSelector((state: RootState) => state.attendance.attendances);
  const meals = useSelector((state: RootState) => state.meals.meals);

  // Basic states
  const [selectedClass, setSelectedClass] = useState<string | null>(null);
  const [selectedClassSectionId, setSelectedClassSectionId] = useState<number | null>(null);
  const [selectedDate, setSelectedDate] = useState<string>(currentDate);
  const [selectedRowKeys, setSelectedRowKeys] = useState<any[]>([]);

  // User role states - memoized to prevent re-renders
  const userRole = useMemo(() => {
    return user?.[0]?.role || 'Teacher';
  }, [user]);

  const isMealSupervisor = useMemo(() => {
    return userRole === 'Meal Supervisor';
  }, [userRole]);

  const schoolId = useMemo(() => {
    let id = user?.[0]?.relatedEntity?.schools?.id;
    if (!id) {
      try {
        const userDetail = localStorage.getItem('userDetail');
        if (userDetail) {
          const parsedUserDetail = JSON.parse(userDetail);
          id = parsedUserDetail?.relatedEntity?.schools?.id;
        }
      } catch (error) {
        console.error('Error getting school ID:', error);
      }
    }
    return id;
  }, [user]);

  // Meal states
  const [mealsSubmitted, setMealsSubmitted] = useState<boolean>(false);
  const [mealDistributionData, setMealDistributionData] = useState<any>(null);
  const [canEditMeals, setCanEditMeals] = useState<boolean>(false);
  const [isEditMode, setIsEditMode] = useState<boolean>(false);

  // Attendance states
  const [attendanceSubmitted, setAttendanceSubmitted] = useState<boolean>(false);

  // Loading states
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

  // Modal states
  const [showSuccess, setShowSuccess] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [showError, setShowError] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  // Submission modal states
  const [showSubmissionModal, setShowSubmissionModal] = useState(false);
  const [submissionData, setSubmissionData] = useState({
    mealServedRemarks: '',
    fileReference: ''
  });

  // No meal modal states
  const [showNoMealModal, setShowNoMealModal] = useState(false);
  const [noMealReason, setNoMealReason] = useState<string>('');
  const [noMealRemarks, setNoMealRemarks] = useState<string>('');
  const [availableReasons, setAvailableReasons] = useState<any[]>([]);
  const [loadingReasons, setLoadingReasons] = useState<boolean>(false);

  // Current meal status from API data
  const [currentMealStatus, setCurrentMealStatus] = useState<any>(null);

  // NEW: For meal supervisor - all available classes and sections from fetchClassApi
  const [allSchoolClasses, setAllSchoolClasses] = useState<any[]>([]);
  const [loadingClasses, setLoadingClasses] = useState<boolean>(false);

  // Load user data from localStorage if needed
  useEffect(() => {
    const accessToken = localStorage.getItem('accessToken');
    const userDetail = localStorage.getItem('userDetail');

    if (accessToken && (!user || user.length === 0) && userDetail) {
      try {
        const parsedUserDetail = JSON.parse(userDetail);
        dispatch(fetchusersSuccess([parsedUserDetail]));
      } catch (error) {
        console.error('Error parsing userDetail from localStorage:', error);
      }
    }
  }, [dispatch, user]);

  // Load meal reasons once
  useEffect(() => {
    const loadMealReasons = async () => {
      setLoadingReasons(true);
      try {
        const reasons = await mealReasonApi();
        if (reasons && Array.isArray(reasons)) {
          const formattedReasons = reasons.map((reason: any) => ({
            id: reason.id,
            value: reason.id.toString(),
            label: reason.reasonText,
            isActive: reason.isActive,
            priority: reason.priority
          }));
          formattedReasons.sort((a, b) => a.priority - b.priority);
          setAvailableReasons(formattedReasons);
        }
      } catch (error) {
        console.error('Error loading meal reasons:', error);
        setAvailableReasons([]);
      } finally {
        setLoadingReasons(false);
      }
    };

    loadMealReasons();
  }, []);

  // NEW: Fetch all school classes for meal supervisor using fetchClassApi
  const fetchAllSchoolClasses = useCallback(async () => {
    if (!isMealSupervisor || !schoolId) return;

    setLoadingClasses(true);
    try {
      const classesResponse = await fetchclassesApi();

      if (classesResponse && Array.isArray(classesResponse)) {
        const formattedClasses = classesResponse.map((classItem: any) => ({
          classId: classItem.id,
          className: classItem.name,
          classKey: classItem.classKey,
          sections: classItem.sectionsArr?.map((sectionName: string, index: number) => ({
            sectionId: classItem.sectionsIdArr?.[index],
            sectionName: sectionName,
            classSectionId: classItem.sectionsIdArr?.[index], // This is what we need for API calls
            fullDisplayName: `${classItem.name} - ${sectionName}`
          })) || []
        }));

        setAllSchoolClasses(formattedClasses);

      } else {
        console.error('Invalid response format from fetchClassApi:', classesResponse);
        setAllSchoolClasses([]);
      }
    } catch (error) {
      console.error('Error fetching all school classes:', error);
      setAllSchoolClasses([]);
    } finally {
      setLoadingClasses(false);
    }
  }, [isMealSupervisor, schoolId]);

  // Load all school classes for meal supervisor
  useEffect(() => {
    if (isMealSupervisor) {
      fetchAllSchoolClasses();
    }
  }, [isMealSupervisor, fetchAllSchoolClasses]);

  // UPDATED: Group sections by class with support for both teacher and meal supervisor
  const groupedClassSections = useMemo(() => {
    if (isMealSupervisor) {
      // For meal supervisor, use fetchClassApi data
      return allSchoolClasses.reduce((grouped: any, classItem: any) => {
        const { className, sections } = classItem;
        if (!grouped[className]) {
          grouped[className] = [];
        }
        grouped[className] = sections.map((section: any) => ({
          className: className,
          sectionName: section.sectionName,
          classSectionId: section.classSectionId,
          fullDisplayName: `${className} - ${section.sectionName}`,
          classId: classItem.classId,
          sectionId: section.sectionId
        }));
        return grouped;
      }, {});
    } else {
      // For teachers, use assigned class sections only (existing logic)
      if (!user?.[0]?.relatedEntity?.schoolClasses) return {};

      const sections = (user[0].relatedEntity.schoolClasses as unknown as any[])
        .reduce((acc: any[], schoolClassObj: any) => {
          if (Array.isArray(schoolClassObj.classSection)) {
            const sections = schoolClassObj.classSection.map((section: any) => {
              const className = schoolClassObj?.schoolClass?.name ||
                schoolClassObj?.name ||
                schoolClassObj?.class?.name ||
                `Class ${schoolClassObj?.classId || 'Unknown'}`;

              const sectionName = section?.name ||
                section?.sectionName ||
                `Section ${section?.id || 'Unknown'}`;

              return {
                className: className,
                sectionName: sectionName,
                classSectionId: section?.id,
                fullDisplayName: `${className} - ${sectionName}`,
                classId: schoolClassObj?.classId || schoolClassObj?.schoolClass?.id,
                sectionId: section?.id
              };
            });
            return [...acc, ...sections];
          }
          return acc;
        }, []);

      return sections.reduce((grouped: any, section: any) => {
        const { className } = section;
        if (!grouped[className]) {
          grouped[className] = [];
        }
        grouped[className].push(section);
        return grouped;
      }, {});
    }
  }, [user, isMealSupervisor, allSchoolClasses]);

  // Get unique class names for dropdown
  const classNames = useMemo(() => {
    return Object.keys(groupedClassSections);
  }, [groupedClassSections]);

  // Get sections for selected class
  const sectionsForSelectedClass = useMemo(() => {
    if (!selectedClass || !groupedClassSections[selectedClass]) return [];
    return groupedClassSections[selectedClass];
  }, [selectedClass, groupedClassSections]);

  // Helper function to get selected section name
  const getSelectedSectionName = useCallback(() => {
    if (!selectedClassSectionId || !sectionsForSelectedClass.length) return 'Unknown Section';
    const section = sectionsForSelectedClass.find((s: any) => s.classSectionId === selectedClassSectionId);
    return section?.sectionName || 'Unknown Section';
  }, [selectedClassSectionId, sectionsForSelectedClass]);

  // FIXED: Single data fetching function for class/section specific data only
  const fetchClassSectionData = useCallback(async (classSectionId: number, date: string) => {
    if (!schoolId || !classSectionId) {
      setShowError(true);
      setErrorMessage('School ID or Class Section ID not found.');
      return;
    }

    setIsLoading(true);
    try {
      // Prepare API parameters for specific class section
      const params = {
        schoolId,
        classSectionId,
        attnDate: date,
        mealDate: date
      };

      // Fetch all data in parallel for the specific class section
      const [attendanceData, mealData, mealStatus] = await Promise.all([
        fetchAttendancesApi(params).catch(() => []),
        fetchMealsApi({ ...params, limit: 1000 }).catch(() => []),
        checkMealSubmissionExistsApi(params).catch(() => ({ exists: false, data: null, canEdit: false }))
      ]);

      // Update Redux store
      dispatch(fetchattendancesSuccess(attendanceData));
      dispatch(fetchmealsSuccess(mealData));

      // Update local states
      setAttendanceSubmitted(attendanceData.length > 0);
      setMealsSubmitted(mealStatus.exists);
      setCanEditMeals(mealStatus.canEdit);
      setMealDistributionData(mealStatus.data);

      // Check for meal reason
      if (mealStatus.data?.mealNotServedReasonId) {
        setCurrentMealStatus(mealStatus.data);
      } else {
        setCurrentMealStatus(null);
      }

      setIsEditMode(false);
      setSelectedRowKeys([]);

    } catch (error: any) {
      console.error("Error fetching class section data:", error);
      setShowError(true);
      setErrorMessage('Failed to load data. Please try again.');
    } finally {
      setIsLoading(false);
    }
  }, [schoolId, dispatch]);

  // Auto-load data when class section or date changes
  useEffect(() => {
    if (selectedClassSectionId) {
      fetchClassSectionData(selectedClassSectionId, selectedDate);
    }
  }, [selectedClassSectionId, selectedDate, fetchClassSectionData]);

  // UPDATED: Initialize with first class and section with proper validation
  useEffect(() => {
    if (classNames.length > 0 && !selectedClass && !loadingClasses) {
      const firstClass = classNames[0];
      setSelectedClass(firstClass);

      const sections = groupedClassSections[firstClass];
      if (sections?.length > 0) {
        const firstSection = sections[0];
        const validClassSectionId = parseInt(firstSection.classSectionId.toString());

        if (!isNaN(validClassSectionId)) {
          setSelectedClassSectionId(validClassSectionId);
        } else {
          console.error('Invalid classSectionId in initialization:', firstSection.classSectionId);
        }
      }
    }
  }, [classNames, selectedClass, groupedClassSections, loadingClasses]);

  // Create table data from attendance - memoized
  const tableData = useMemo(() => {
    if (!Array.isArray(attendance)) return [];

    const presentStudents = attendance.filter((item: any) =>
      (item.attnTypeId === 1 && item.attendance === true)
    );

    return presentStudents.map((item: any, idx: number) => {
      const mealDistributed = meals?.find((meal: any) =>
        parseInt(meal?.studentId) === parseInt(item?.studentId)
      );

      return {
        key: item?.studentId || idx,
        rollNo: item?.student?.rollNo || "",
        firstName: item?.student?.firstName || "",
        name: item?.student?.name || "",
        id: item?.studentId,
        studentId: item?.studentId,
        profileImage: item?.student?.avatarUrl || item?.student?.img || "",
        mealDistributed: !!mealDistributed,
        attendanceRecord: item,
      };
    });
  }, [attendance, meals]);

  // Get current no meal reason from API data
  const currentNoMealReason = useMemo(() => {
    if (!currentMealStatus || !currentMealStatus.mealNotServedReasonId) return null;

    const reasonId = currentMealStatus.mealNotServedReasonId;
    const reason = availableReasons.find(r => r.id === reasonId);

    return {
      reasonId,
      reasonText: reason?.label || currentMealStatus.notServedReason?.reasonText || 'Unknown Reason',
      remarks: currentMealStatus.mealServedRemarks || '',
      timestamp: currentMealStatus.updatedAt || currentMealStatus.createdAt
    };
  }, [currentMealStatus, availableReasons]);

  // Auto-select students who already have meals distributed
  useEffect(() => {
    if ((canEditMeals || !mealsSubmitted) && tableData.length > 0 && !currentNoMealReason) {
      const studentsWithMeals = tableData.filter((item: any) => item.mealDistributed);
      setSelectedRowKeys(studentsWithMeals);
    } else {
      setSelectedRowKeys([]);
    }
  }, [tableData, mealsSubmitted, canEditMeals, currentNoMealReason]);

  // FIXED: Single meal operation function with proper type conversion
  const handleMealOperation = useCallback(async (operationType: 'submit' | 'edit' | 'reason', data: any) => {
    if (!schoolId || !selectedClassSectionId) {
      throw new Error('School ID or Class Section ID not found');
    }

    let payload: any = {
      classSectionId: parseInt(selectedClassSectionId.toString()),
      schoolId: parseInt(schoolId.toString()),
      mealDate: selectedDate,
      mealType: 1,
      recordedVia: 1,
      fileReference: { url: data.fileReference || "https://example.com/file.jpg" },
      mealServedTime: new Date().toISOString(),
      mealServedBy: parseInt(user?.[0]?.id?.toString() || "1"),
      mealServedRemarks: data.mealServedRemarks || "",
    };

    if (operationType === 'reason') {
      payload.mealServed = false;
      payload.studentIds = [];
      payload.mealNotServedReasonId = parseInt(data.reasonId.toString());
    } else {
      payload.mealServed = true;
      payload.studentIds = data.studentIds.map((id: any) => parseInt(id.toString()));
    }

    return await setMealApi(payload);
  }, [schoolId, selectedClassSectionId, selectedDate, user]);

  // Handle submit click
  const handleSubmitClick = useCallback(() => {
    if (!selectedClassSectionId) {
      setShowError(true);
      setErrorMessage('Please select a class section.');
      return;
    }

    if (selectedRowKeys.length === 0) {
      setShowError(true);
      setErrorMessage('Please select students for meal distribution.');
      return;
    }

    if (mealsSubmitted && !canEditMeals) {
      setShowError(true);
      setErrorMessage('Meals have already been edited and cannot be modified further.');
      return;
    }

    const isEdit = mealsSubmitted && canEditMeals;
    setIsEditMode(isEdit);

    if (isEdit && mealDistributionData) {
      setSubmissionData({
        mealServedRemarks: mealDistributionData.mealServedRemarks || '',
        fileReference: ''
      });
    } else {
      setSubmissionData({
        mealServedRemarks: '',
        fileReference: ''
      });
    }

    setShowSubmissionModal(true);
  }, [selectedClassSectionId, selectedRowKeys, mealsSubmitted, canEditMeals, mealDistributionData]);

  // Submit meals function with better error handling
  const submitMeals = useCallback(async () => {
    setIsSubmitting(true);
    setShowSubmissionModal(false);

    try {
      const studentIds = selectedRowKeys.map((student: any) =>
        parseInt((student.studentId || student.id).toString())
      );

      const operationType = isEditMode ? 'edit' : 'submit';
      await handleMealOperation(operationType, {
        studentIds,
        mealServedRemarks: submissionData.mealServedRemarks,
        fileReference: submissionData.fileReference
      });

      setSuccessMessage(`Meal distribution ${isEditMode ? 'updated' : 'submitted'} successfully!`);
      setShowSuccess(true);

      setTimeout(async () => {
        if (selectedClassSectionId) {
          await fetchClassSectionData(selectedClassSectionId, selectedDate);
        }
      }, 1000);

      setSubmissionData({ mealServedRemarks: '', fileReference: '' });

    } catch (error: any) {
      console.error(`Error ${isEditMode ? 'updating' : 'submitting'} meals:`, error);

      const errorMessage = error.response?.data?.message;
      const isValidationError = Array.isArray(errorMessage) && errorMessage.some((msg: string) =>
        msg.includes('must be') || msg.includes('required') || msg.includes('invalid')
      );

      if (isValidationError) {
        setShowError(true);
        setErrorMessage(`Validation Error: ${Array.isArray(errorMessage) ? errorMessage.join(', ') : errorMessage}`);
      } else {
        let errorMsg = error.message || `Failed to ${isEditMode ? 'update' : 'submit'} meal distribution`;

        if (errorMsg.includes('already exists') || errorMsg.includes('duplicate')) {
          setSuccessMessage('Meal distribution was processed successfully!');
          setShowSuccess(true);

          setTimeout(async () => {
            if (selectedClassSectionId) {
              await fetchClassSectionData(selectedClassSectionId, selectedDate);
            }
          }, 1000);
        } else {
          setShowError(true);
          setErrorMessage(errorMsg);
          setTimeout(async () => {
            if (selectedClassSectionId) {
              await fetchClassSectionData(selectedClassSectionId, selectedDate);
            }
          }, 2000);
        }
      }
    } finally {
      setIsSubmitting(false);
    }
  }, [selectedRowKeys, submissionData, isEditMode, handleMealOperation, selectedClassSectionId, selectedDate, fetchClassSectionData]);

  const handleNoMealReasonSubmit = useCallback(async () => {
    if (!noMealReason) {
      setShowError(true);
      setErrorMessage('Please select a reason for not providing meals.');
      return;
    }

    if (!selectedClassSectionId) {
      setShowError(true);
      setErrorMessage('Please select a class section.');
      return;
    }

    if (isNaN(parseInt(selectedClassSectionId.toString()))) {
      setShowError(true);
      setErrorMessage('Invalid class section selected. Please refresh and try again.');
      return;
    }

    const selectedReasonData = availableReasons.find(r => r.value === noMealReason);
    const isOtherReason = selectedReasonData?.label?.toLowerCase().includes('other');

    if (isOtherReason && !noMealRemarks.trim()) {
      setShowError(true);
      setErrorMessage('Please provide remarks for "Other" reason.');
      return;
    }

    setIsSubmitting(true);
    setShowNoMealModal(false);

    try {
      await handleMealOperation('reason', {
        reasonId: noMealReason,
        mealServedRemarks: noMealRemarks.trim() || selectedReasonData?.label || "",
      });

      setShowSuccess(true);
      setSuccessMessage('No meal reason recorded successfully!');
      setTimeout(async () => {
        if (selectedClassSectionId) {
          await fetchClassSectionData(selectedClassSectionId, selectedDate);
        }
      }, 1000);

    } catch (error: any) {
      console.error('Error recording no meal reason:', error);

      const errorMessage = error.response?.data?.message;
      const isValidationError = Array.isArray(errorMessage) && errorMessage.some((msg: string) =>
        msg.includes('must be') || msg.includes('required') || msg.includes('invalid')
      );

      if (isValidationError) {
        setShowError(true);
        setErrorMessage(`Validation Error: ${Array.isArray(errorMessage) ? errorMessage.join(', ') : errorMessage}`);
      } else {
        let errorMsg = error.message || 'Failed to record no meal reason';

        if (errorMsg.includes('already exists') || errorMsg.includes('duplicate')) {
          setShowSuccess(true);
          setSuccessMessage('No meal reason was processed successfully!');

          setTimeout(async () => {
            if (selectedClassSectionId) {
              await fetchClassSectionData(selectedClassSectionId, selectedDate);
            }
          }, 1000);
        } else {
          setShowError(true);
          setErrorMessage(errorMsg);

          setTimeout(async () => {
            if (selectedClassSectionId) {
              await fetchClassSectionData(selectedClassSectionId, selectedDate);
            }
          }, 2000);
        }
      }
    } finally {
      setIsSubmitting(false);
      setNoMealReason('');
      setNoMealRemarks('');
    }
  }, [noMealReason, noMealRemarks, availableReasons, handleMealOperation, selectedClassSectionId, selectedDate, fetchClassSectionData]);

  // Handle class change
  const handleClassChange = useCallback((className: string) => {
    setSelectedClass(className);
    setSelectedClassSectionId(null);

    const sections = groupedClassSections[className] || [];
    if (sections.length > 0) {
      setSelectedClassSectionId(sections[0].classSectionId);
    }
  }, [groupedClassSections]);

  // Handle section click
  const handleSectionClick = useCallback((classSectionId: number) => {
    setSelectedClassSectionId(classSectionId);
  }, []);

  // UPDATED: Handle date change with class refresh for meal supervisor
  const handleDateChange = useCallback((date: any, dateString: string | string[]) => {
    const dateStr = Array.isArray(dateString) ? dateString[0] : dateString;
    if (dateStr) {
      setSelectedDate(dateStr);
      // For meal supervisor, refresh available classes for the new date
      if (isMealSupervisor) {
        // Refresh the classes data since different dates might have different attendance
        fetchAllSchoolClasses();
        // Reset selection to allow user to reselect
        setSelectedClass(null);
        setSelectedClassSectionId(null);
      }
    }
  }, [isMealSupervisor, fetchAllSchoolClasses]);

  // Enhanced columns
  const columns = useMemo(() => {
    return [
      {
        title: "Roll No",
        dataIndex: "rollNo",
        sorter: (a: TableData, b: TableData) => a.rollNo.length - b.rollNo.length,
      },
      {
        title: "Name",
        dataIndex: "firstName",
        render: (text: string, record: any) => (
          <div className="d-flex align-items-center">
            <Link to="#" className="avatar avatar-md">
              <ImageWithBasePath
                src={record.profileImage || "assets/img/teachers/teacher-01.jpg"}
                className="img-fluid rounded-circle"
                alt="img"
              />
            </Link>
            <div className="ms-2">
              <p className="text-dark mb-0">
                <Link to="#">{record.name || text}</Link>
              </p>
            </div>
          </div>
        ),
        sorter: (a: TableData, b: TableData) => a.name.length - b.name.length,
      },
    ];
  }, []);

  const handleRowSelection = useCallback((keys: any[]) => {
    if (mealsSubmitted && !canEditMeals) return;
    if (currentNoMealReason) return;

    const selectedObjects = keys.map((key: any) => {
      const record = tableData.find((item: any) => item.key === key || item.id === key);
      return record || { key, id: key };
    });
    setSelectedRowKeys(selectedObjects);
  }, [tableData, mealsSubmitted, canEditMeals, currentNoMealReason]);

  // Get meal status summary
  const statusSummary = useMemo(() => {
    const presentCount = tableData.length;
    const mealsProvidedCount = tableData.filter(item => item.mealDistributed).length;
    return {
      presentCount,
      mealsProvidedCount,
      mealsPendingCount: presentCount - mealsProvidedCount
    };
  }, [tableData]);

  // Determine button state and text
  const buttonState = useMemo(() => {
    if (isLoading || isSubmitting) {
      return {
        disabled: true,
        className: 'btn-secondary',
        icon: 'ti-loader-2 animate-spin',
        text: isSubmitting ? 'Processing...' : 'Loading...'
      };
    }

    if (tableData.length === 0) {
      return {
        disabled: true,
        className: 'btn-secondary',
        icon: 'ti-users-off',
        text: 'No Present Students'
      };
    }

    if (currentNoMealReason) {
      return {
        disabled: true,
        className: 'btn-danger',
        icon: 'ti-ban',
        text: 'No Meals - Reason Recorded'
      };
    }

    if (mealsSubmitted && !canEditMeals) {
      return {
        disabled: true,
        className: 'btn-success',
        icon: 'ti-lock',
        text: 'Meals Locked (Already Edited)'
      };
    }

    if (mealsSubmitted && canEditMeals) {
      return {
        disabled: selectedRowKeys.length === 0,
        className: selectedRowKeys.length === 0 ? 'btn-secondary' : 'btn-warning',
        icon: 'ti-edit',
        text: `Edit Meals (${selectedRowKeys.length} selected)`
      };
    }

    return {
      disabled: selectedRowKeys.length === 0,
      className: selectedRowKeys.length === 0 ? 'btn-secondary' : 'btn-primary',
      icon: 'ti-square-rounded-plus',
      text: `Submit Meals (${selectedRowKeys.length} selected)`
    };
  }, [isLoading, isSubmitting, tableData.length, currentNoMealReason, mealsSubmitted, canEditMeals, selectedRowKeys.length]);

  // Export data preparation - memoized
  const exportData = useMemo(() => {
    return tableData.map((item: any) => ({
      ...item,
      fullName: `${item.firstName} ${item.lastName}`.trim() || item.name,
      classSection: `${item.schoolClass} - ${item.classSection}`,
      mealLabel: item.mealDistributed ? "✓ Meal Provided" : "⏳ Meal Pending",
      exportDate: selectedDate,
    }));
  }, [tableData, selectedDate]);

  // Export columns configuration
  const exportColumns: ExportColumn[] = [
    { title: "Roll No", dataIndex: "rollNo" },
    {
      title: "Student Name",
      dataIndex: "fullName",
      render: (_, row) => row.fullName || row.firstName || row.name || "Unknown",
    },
    {
      title: "Class & Section",
      dataIndex: "classSection",
      render: (_, row) => `${row.schoolClass} - ${row.classSection}`,
    },
    {
      title: "Meal Status",
      dataIndex: "mealStatus",
      render: (_, row) => row.mealDistributed ? "Meal Provided" : "Meal Pending",
    },
    {
      title: "Date",
      dataIndex: "exportDate",
    },
  ];

  const handleExportPDF = () => {
    exportToPDF(exportData, exportColumns, "Student-Meals.pdf");
  };

  const handleExportExcel = () => {
    exportToExcel(exportData, exportColumns, "Student-Meals.xlsx");
  };


  return (
    <div>
      <div className="page-wrapper">
        <div className="content">
          {/* Page Header */}
          <div className="d-md-flex d-block align-items-center justify-content-between mb-3">
            <div className="my-auto mb-2">
              <h3 className="page-title mb-1">
                Student Meals Management
                {isMealSupervisor && <span className="badge bg-info ms-2">Meal Supervisor</span>}
              </h3>
              <nav>
                <ol className="breadcrumb mb-0">
                  <li className="breadcrumb-item">
                    Dashboard
                  </li>
                  <li className="breadcrumb-item">
                    <Link to="#">Meals</Link>
                  </li>
                  <li className="breadcrumb-item active" aria-current="page">
                    Student Meals
                  </li>
                </ol>
              </nav>
            </div>
            <div className="d-flex my-xl-auto right-content align-items-center flex-wrap">
              <TooltipOption
                onExportPDF={handleExportPDF}
                onExportExcel={handleExportExcel}
              />

              {/* Record No Meal Reason button */}
              {!isLoading && attendanceSubmitted && !mealsSubmitted && !currentNoMealReason && (
                <div className="mb-2 me-2">
                  <button
                    type="button"
                    className="btn btn-outline-warning"
                    onClick={() => setShowNoMealModal(true)}
                    disabled={isSubmitting}
                  >
                    <i className="ti ti-alert-circle me-2" />
                    Record No Meal Reason
                  </button>
                </div>
              )}

              {/* Submit/Edit button */}
              <div className="mb-2">
                <button
                  type="button"
                  className={`btn d-flex align-items-center ${buttonState.className}`}
                  onClick={handleSubmitClick}
                  disabled={buttonState.disabled}
                >
                  <i className={`ti ${buttonState.icon} me-2`} />
                  {buttonState.text}
                </button>
              </div>
            </div>
          </div>

          {/* Class and Section Selection */}
          <div className="row mb-3 align-items-stretch">
            {/* Class Selection */}
            <div className="col-md-3 col-lg-3">
              <div className="form-group h-100 d-flex flex-column">
                <label htmlFor="classSelect" className="form-label mb-2">
                  Select Class {isMealSupervisor && <span className="text-info">(All School Classes)</span>}
                </label>
                <div className="flex-grow-1 d-flex align-items-start">
                  {loadingClasses ? (
                    <div className="w-100 d-flex justify-content-center align-items-center" style={{ minHeight: '40px' }}>
                      <div className="spinner-border spinner-border-sm me-2" role="status">
                        <span className="visually-hidden">Loading...</span>
                      </div>
                      Loading classes...
                    </div>
                  ) : (
                    <Select
                      id="classSelect"
                      placeholder="Select a class"
                      value={selectedClass}
                      onChange={handleClassChange}
                      className="w-100"
                      size="large"
                      style={{ minHeight: '40px' }}
                      disabled={loadingClasses}
                    >
                      {classNames.map((className: string) => (
                        <Option key={className} value={className}>
                          {className}
                        </Option>
                      ))}
                    </Select>
                  )}
                </div>
              </div>
            </div>

            {/* Sections Selection */}
            <div className="col-md-9 col-lg-9">
              {selectedClass && sectionsForSelectedClass.length > 0 ? (
                <div className="form-group h-100 d-flex flex-column">
                  <label className="form-label mb-2">Sections for {selectedClass}</label>
                  <div className="flex-grow-1">
                    <div
                      className="d-flex gap-2 pb-2 h-100 align-items-start"
                      style={{
                        overflowX: 'auto',
                        whiteSpace: 'nowrap',
                        scrollbarWidth: 'thin',
                        scrollbarColor: '#888 #f1f1f1',
                        minHeight: '60px'
                      }}
                    >
                      {sectionsForSelectedClass.map((section: any, idx: number) => (
                        <div
                          key={idx}
                          className={`card flex-shrink-0 ${selectedClassSectionId === section.classSectionId ? "border-primary bg-light" : "border-secondary"
                            }`}
                          style={{
                            cursor: "pointer",
                            minWidth: "120px",
                            width: "120px",
                            height: "40px",
                            transition: "all 0.2s ease"
                          }}
                          onClick={() => handleSectionClick(section.classSectionId)}
                        >
                          <div className="card-body d-flex flex-column justify-content-center align-items-center p-2 h-100">
                            <div className="fw-bold text-center" style={{ fontSize: '0.9rem', lineHeight: '1.2' }}>
                              {section.sectionName}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              ) : selectedClass ? (
                <div className="form-group h-100 d-flex flex-column">
                  <label className="form-label mb-2">Sections</label>
                  <div className="flex-grow-1 d-flex align-items-center justify-content-center">
                    <div className="text-muted">No sections available for {selectedClass}</div>
                  </div>
                </div>
              ) : (
                <div className="form-group h-100 d-flex flex-column">
                  <label className="form-label mb-2">Sections</label>
                  <div className="flex-grow-1 d-flex align-items-center justify-content-center">
                    <div className="text-muted">
                      {loadingClasses ? 'Loading classes...' : 'Please select a class first'}
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Meal Status Summary */}
          {!isLoading && attendanceSubmitted && (
            <div className="row mb-3">
              <div className="col-12">
                <div className="card">
                  <div className="card-body">
                    <h5 className="card-title mb-3">
                      Meal Distribution Summary for {selectedClass} - {getSelectedSectionName()} on {selectedDate}
                    </h5>
                    <div className="row">
                      <div className="col-md-3">
                        <div className="d-flex align-items-center">
                          <div className="avatar avatar-md bg-primary-light me-3">
                            <i className="ti ti-users text-primary"></i>
                          </div>
                          <div>
                            <h6 className="mb-0">{statusSummary.presentCount}</h6>
                            <small className="text-muted">Present Students</small>
                          </div>
                        </div>
                      </div>
                      <div className="col-md-3">
                        <div className="d-flex align-items-center">
                          <div className="avatar avatar-md bg-success-light me-3">
                            <i className="ti ti-check text-success"></i>
                          </div>
                          <div>
                            <h6 className="mb-0">{statusSummary.mealsProvidedCount}</h6>
                            <small className="text-muted">Meals Provided</small>
                          </div>
                        </div>
                      </div>
                      <div className="col-md-3">
                        <div className="d-flex align-items-center">
                          <div className="avatar avatar-md bg-warning-light me-3">
                            <i className="ti ti-clock text-warning"></i>
                          </div>
                          <div>
                            <h6 className="mb-0">{statusSummary.mealsPendingCount}</h6>
                            <small className="text-muted">Meals Pending</small>
                          </div>
                        </div>
                      </div>
                      <div className="col-md-3">
                        <div className="d-flex align-items-center">
                          <div className="avatar avatar-md bg-info-light me-3">
                            <i className="ti ti-percentage text-info"></i>
                          </div>
                          <div>
                            <h6 className="mb-0">
                              {statusSummary.presentCount > 0
                                ? Math.round((statusSummary.mealsProvidedCount / statusSummary.presentCount) * 100)
                                : 0}%
                            </h6>
                            <small className="text-muted">Coverage</small>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Show no meal reason if exists */}
                    {currentNoMealReason && (
                      <div className="mt-3">
                        <div className="alert alert-info">
                          <div className="d-flex align-items-start">
                            <i className="ti ti-info-circle me-2 mt-1"></i>
                            <div>
                              <strong>Reason for No Meals:</strong>
                              <p className="mb-1">
                                {currentNoMealReason.reasonText}
                              </p>
                              {currentNoMealReason.remarks && (
                                <p className="mb-1"><strong>Remarks:</strong> {currentNoMealReason.remarks}</p>
                              )}
                              <small className="text-muted">
                                Recorded on: {new Date(currentNoMealReason.timestamp).toLocaleString()}
                              </small>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Student List */}
          <div className="card">
            <div className="card-header d-flex align-items-center justify-content-between flex-wrap pb-0">
              <h4 className="mb-3">
                Present Students - Meal Distribution
                {isMealSupervisor && <span className="text-info ms-2">(Meal Supervisor Access)</span>}
              </h4>
              <div className="d-flex align-items-center flex-wrap">
                <div className="input-icon-start mb-3 me-2 position-relative">
                  <div className="date-pic">
                    <DatePicker
                      className="form-control datetimepicker"
                      format={{ format: "YYYY-MM-DD", type: "mask" }}
                      placeholder={selectedDate}
                      onChange={handleDateChange}
                    />
                    <span className="cal-icon">
                      <i className="ti ti-calendar" />
                    </span>
                  </div>
                </div>
              </div>
            </div>
            <div className="card-body p-0 py-3">
              {/* Show message if attendance not taken */}
              {!isLoading && !attendanceSubmitted && (
                <div className="text-center py-5">
                  <div className="mb-3">
                    <i className="ti ti-calendar-off" style={{ fontSize: '3rem', color: '#6c757d' }}></i>
                  </div>
                  <h5 className="text-muted mb-2">Attendance Not Taken</h5>
                  <p className="text-muted">
                    Please take attendance for <strong>{selectedDate}</strong> in{' '}
                    <strong>{selectedClass || 'the selected class'}</strong> before distributing meals.
                  </p>
                  {!isMealSupervisor &&
                    <div className="mt-3">
                      <Link to="/hrm/student-attendance" className="btn btn-outline-primary me-2">
                        <i className="ti ti-calendar-check me-2"></i>
                        Take Attendance
                      </Link>
                    </div>
                  }
                </div>
              )}

              {/* Show message for no meal reason recorded */}
              {!isLoading && tableData.length > 0 && currentNoMealReason && (
                <div className="alert alert-danger mt-3 mx-3">
                  <div className="d-flex align-items-center">
                    <i className="ti ti-ban me-2" style={{ fontSize: '1.5rem', color: '#dc3545' }}></i>
                    <div>
                      <h6 className="mb-1" style={{ color: '#721c24' }}>No Meal Distribution - Reason Recorded</h6>
                      <p className="mb-0" style={{ color: '#721c24' }}>
                        A reason for not providing meals has been recorded for <strong>{selectedDate}</strong>.
                        Meal distribution is now disabled for this date and class.
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* Show message for submitted meals */}
              {!isLoading && tableData.length > 0 && mealsSubmitted && !canEditMeals && !currentNoMealReason && (
                <div className="alert alert-success mt-3 mx-3">
                  <div className="d-flex align-items-center">
                    <i className="ti ti-check-circle me-2" style={{ fontSize: '1.5rem', color: '#28a745' }}></i>
                    <div>
                      <h6 className="mb-1" style={{ color: '#155724' }}>Meals Successfully Submitted & Locked</h6>
                      <p className="mb-0" style={{ color: '#155724' }}>
                        Meal distribution for <strong>{selectedDate}</strong> has been completed and edited once.
                        This page is now in read-only mode to prevent further modifications.
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* Show message for editable meals */}
              {!isLoading && tableData.length > 0 && mealsSubmitted && canEditMeals && !currentNoMealReason && (
                <div className="alert alert-warning mt-3 mx-3">
                  <div className="d-flex align-items-center">
                    <i className="ti ti-edit me-2" style={{ fontSize: '1.5rem', color: '#856404' }}></i>
                    <div>
                      <h6 className="mb-1" style={{ color: '#856404' }}>Meals Can Be Edited Once</h6>
                      <p className="mb-0" style={{ color: '#856404' }}>
                        Meal distribution for <strong>{selectedDate}</strong> has been submitted but can be edited once more.
                        After editing, it will be locked permanently.
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* Show message if no present students */}
              {!isLoading && attendanceSubmitted && tableData.length === 0 && (
                <div className="text-center py-5">
                  <div className="mb-3">
                    <i className="ti ti-users-off" style={{ fontSize: '3rem', color: '#6c757d' }}></i>
                  </div>
                  <h5 className="text-muted mb-2">No Present Students</h5>
                  <p className="text-muted">
                    No students were present on <strong>{selectedDate}</strong> in{' '}
                    <strong>{selectedClass || 'the selected class'}</strong>.
                  </p>
                </div>
              )}

              {/* Show loading state */}
              {isLoading && (
                <div className="text-center py-5">
                  <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Loading...</span>
                  </div>
                  <p className="text-muted mt-2">Loading present students...</p>
                </div>
              )}

              {/* Student List */}
              {!isLoading && attendanceSubmitted && tableData.length > 0 && (
                <Table
                  dataSource={tableData}
                  columns={columns}
                  Selection={(!mealsSubmitted || canEditMeals) && !currentNoMealReason}
                  selectedRowKeys={(mealsSubmitted && !canEditMeals) || currentNoMealReason ? [] : selectedRowKeys.map((item: any) => item.key || item.id)}
                  setSelectedRowKeys={(mealsSubmitted && !canEditMeals) || currentNoMealReason ? undefined : handleRowSelection}
                  onRow={(record: any) => ({
                    onClick: (event: React.MouseEvent) => {
                      if ((mealsSubmitted && !canEditMeals) || currentNoMealReason) {
                        event.stopPropagation();
                        event.preventDefault();
                      }
                    },
                    style: ((mealsSubmitted && !canEditMeals) || currentNoMealReason) ? {
                      pointerEvents: 'none',
                      opacity: 0.7,
                      backgroundColor: '#f8f9fa',
                      color: '#6c757d',
                      cursor: 'not-allowed'
                    } : {},
                  })}
                />
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Submission Modal */}
      {showSubmissionModal && (
        <div className="modal fade show d-block mt-4" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  {isEditMode ? 'Edit Meal Distribution' : 'Submit Meal Distribution'}
                </h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => {
                    setShowSubmissionModal(false);
                    setSubmissionData({ mealServedRemarks: '', fileReference: '' });
                  }}
                  disabled={isSubmitting}
                ></button>
              </div>
              <div className="modal-body">
                <div className="mb-4">
                  <div className={`alert ${isEditMode ? 'alert-warning' : 'alert-info'}`}>
                    <i className={`ti ${isEditMode ? 'ti-edit' : 'ti-info-circle'} me-2`}></i>
                    You are about to {isEditMode ? 'edit' : 'submit'} meal distribution for{' '}
                    <strong>{selectedRowKeys.length}</strong> students for <strong>{selectedDate}</strong>.
                    {isEditMode && (
                      <div className="mt-2 fw-bold">
                        ⚠️ This is your only edit opportunity. After this, meals will be locked permanently.
                      </div>
                    )}
                  </div>
                </div>

                <div className="mb-3">
                  <label className="form-label">
                    Meal Served Remarks <span className="text-muted">(Optional)</span>
                  </label>
                  <textarea
                    className="form-control"
                    rows={2}
                    placeholder={`Enter any meal served remarks about this meal ${isEditMode ? 'update' : 'distribution'}...`}
                    value={submissionData.mealServedRemarks}
                    onChange={(e) => setSubmissionData(prev => ({ ...prev, mealServedRemarks: e.target.value }))}
                    disabled={isSubmitting}
                  />
                </div>

                <div className="mb-3">
                  <label className="form-label">
                    File Reference <span className="text-muted">(Optional)</span>
                  </label>
                  <input
                    type="file"
                    className="form-control"
                    accept="image/*,.pdf,.doc,.docx"
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (file) {
                        setSubmissionData(prev => ({ ...prev, fileReference: file.name }));
                      } else {
                        setSubmissionData(prev => ({ ...prev, fileReference: '' }));
                      }
                    }}
                    disabled={isSubmitting}
                  />
                  {submissionData.fileReference && (
                    <small className="text-success mt-1 d-block">
                      <i className="ti ti-check me-1"></i>
                      <strong>Selected file:</strong> {submissionData.fileReference}
                    </small>
                  )}
                </div>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary me-2"
                  onClick={() => {
                    setShowSubmissionModal(false);
                    setSubmissionData({ mealServedRemarks: '', fileReference: '' });
                  }}
                  disabled={isSubmitting}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className={`btn ${isEditMode ? 'btn-warning' : 'btn-primary'}`}
                  onClick={submitMeals}
                  disabled={isSubmitting}
                >
                  {isSubmitting ? (
                    <>
                      <i className="ti ti-loader-2 me-2 animate-spin"></i>
                      {isEditMode ? 'Updating...' : 'Submitting...'}
                    </>
                  ) : (
                    <>
                      <i className={`ti ${isEditMode ? 'ti-edit' : 'ti-check'} me-2`}></i>
                      {isEditMode ? 'Confirm Edit' : 'Confirm Submit'}
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* No Meal Reason Modal */}
      {showNoMealModal && (
        <div className="modal fade show d-block mt-4" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Record No Meal Reason</h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setShowNoMealModal(false)}
                  disabled={isSubmitting}
                ></button>
              </div>
              <div className="modal-body">
                <div className="mb-4">
                  <div className="alert alert-warning">
                    <i className="ti ti-alert-circle me-2"></i>
                    Recording reason for not providing meals on <strong>{selectedDate}</strong> for{' '}
                    <strong>{selectedClass} - {getSelectedSectionName()}</strong>.
                    This will be stored in the database.
                  </div>
                </div>

                <div className="mb-3">
                  <label className="form-label">
                    Reason for No Meals <span className="text-danger">*</span>
                  </label>
                  {loadingReasons ? (
                    <div className="form-control d-flex align-items-center">
                      <div className="spinner-border spinner-border-sm me-2" role="status">
                        <span className="visually-hidden">Loading...</span>
                      </div>
                      Loading reasons...
                    </div>
                  ) : (
                    <select
                      className="form-control form-select"
                      value={noMealReason}
                      onChange={(e) => setNoMealReason(e.target.value)}
                      style={{ cursor: 'pointer' }}
                      disabled={isSubmitting}
                    >
                      <option value="">Select a reason...</option>
                      {availableReasons.map((reason) => (
                        <option key={reason.value} value={reason.value}>
                          {reason.label}
                        </option>
                      ))}
                    </select>
                  )}
                </div>

                {/* Show remarks field for "Other" reason or when remarks are needed */}
                {(noMealReason && (availableReasons.find(r => r.value === noMealReason)?.label?.toLowerCase().includes('other') || noMealRemarks)) && (
                  <div className="mb-3">
                    <label className="form-label">
                      Remarks {availableReasons.find(r => r.value === noMealReason)?.label?.toLowerCase().includes('other') && <span className="text-danger">*</span>}
                    </label>
                    <textarea
                      className="form-control"
                      rows={3}
                      placeholder={
                        availableReasons.find(r => r.value === noMealReason)?.label?.toLowerCase().includes('other')
                          ? "Please specify the reason..."
                          : "Enter any additional remarks..."
                      }
                      value={noMealRemarks}
                      onChange={(e) => setNoMealRemarks(e.target.value)}
                      disabled={isSubmitting}
                    />
                  </div>
                )}
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary me-2"
                  onClick={() => setShowNoMealModal(false)}
                  disabled={isSubmitting}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn-warning"
                  onClick={handleNoMealReasonSubmit}
                  disabled={isSubmitting || !noMealReason}
                >
                  {isSubmitting ? (
                    <>
                      <i className="ti ti-loader-2 me-2 animate-spin"></i>
                      Recording...
                    </>
                  ) : (
                    <>
                      <i className="ti ti-check me-2"></i>
                      Record Reason
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Success Modal */}
      <SuccessModal
        show={showSuccess}
        message={successMessage}
        onClose={() => setShowSuccess(false)}
      />

      {/* Error Modal */}
      <ErrorModal
        show={showError}
        message={errorMessage}
        onClose={() => setShowError(false)}
      />
    </div>
  );
};

export default StudentMeals;